import React, { useState, useEffect } from 'react';
import { 
  Heart, 
 
  Clock, 
  Music, 
  Play, 
  Pause,
  MoreHorizontal,
  Plus,
  Search,
  Filter,
  Trash2,
  X,
  RotateCcw
} from 'lucide-react';
import dataManager from '../utils/dataManager';
import ConfirmDialog from './ConfirmDialog';
import CreatePlaylistDialog from './CreatePlaylistDialog';

const LibraryPage = ({ initialSelectedPlaylist = 'favorites', onPlaylistChange, onPlaylistsChange, onPlaySong, onAddSongsToQueue }) => {
  const [selectedPlaylist, setSelectedPlaylist] = useState(initialSelectedPlaylist);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSong, setCurrentSong] = useState(null);
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showToast, setShowToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, playlist: null });
  const [playAllConfirmDialog, setPlayAllConfirmDialog] = useState({ open: false });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [clearRecentDialog, setClearRecentDialog] = useState({ open: false });
  const [filterOptions, setFilterOptions] = useState({
    searchTerm: '',
    artist: '',
    album: '',
    platform: 'all'
  });

  // 初始化选中的歌单
  useEffect(() => {
    setSelectedPlaylist(initialSelectedPlaylist);
  }, [initialSelectedPlaylist]);

  // 歌单切换时通知父组件
  useEffect(() => {
    if (onPlaylistChange) {
      onPlaylistChange(selectedPlaylist);
    }
  }, [selectedPlaylist, onPlaylistChange]);

  // 加载数据
  useEffect(() => {
    loadData();
  }, []);

  // 重新加载数据
  const loadData = () => {
    const playlists = dataManager.getPlaylists();
    setUserPlaylists(playlists);
  };

  // 显示提示消息
  const showToastMessage = (message, type = 'success') => {
    setShowToast({ message, type });
    setTimeout(() => setShowToast(null), 2000);
  };

  // 默认歌单配置
  const getDefaultPlaylists = () => {
    const favorites = dataManager.getFavorites();
    const recent = dataManager.getRecent();

    return [
      {
        id: 'favorites',
        name: '我喜欢的音乐',
        icon: Heart,
        count: favorites.length,
        color: 'from-red-500 to-pink-500',
        description: '你收藏的所有音乐',
        songs: favorites
      },
      {
        id: 'recent',
        name: '最近播放',
        icon: Clock,
        count: recent.length,
        color: 'from-blue-500 to-indigo-500',
        description: '最近听过的歌曲',
        songs: recent
      }
    ];
  };

  // 获取当前选中的歌单数据
  const getCurrentPlaylistData = () => {
    const defaultPlaylists = getDefaultPlaylists();
    const allPlaylists = [...defaultPlaylists, ...userPlaylists];
    
    const playlist = allPlaylists.find(p => p.id === selectedPlaylist);
    if (!playlist) return null;

    // 如果是用户歌单，需要获取歌曲数据
    if (playlist.songs === undefined) {
      const playlistData = dataManager.getPlaylist(selectedPlaylist);
      return {
        ...playlist,
        songs: playlistData?.songs || [],
        count: playlistData?.songs?.length || 0
      };
    }

    return playlist;
  };

  const currentPlaylist = getCurrentPlaylistData();
  const allSongs = currentPlaylist?.songs || [];
  
  // 筛选歌曲
  const songs = allSongs.filter(song => {
    const matchesSearch = !filterOptions.searchTerm || 
      song.name?.toLowerCase().includes(filterOptions.searchTerm.toLowerCase()) ||
      song.artist?.toLowerCase().includes(filterOptions.searchTerm.toLowerCase());
    
    const matchesArtist = !filterOptions.artist || 
      song.artist?.toLowerCase().includes(filterOptions.artist.toLowerCase());
    
    const matchesAlbum = !filterOptions.album || 
      song.album?.toLowerCase().includes(filterOptions.album.toLowerCase());
    
    const matchesPlatform = filterOptions.platform === 'all' || 
      song.platform === filterOptions.platform;
    
    return matchesSearch && matchesArtist && matchesAlbum && matchesPlatform;
  });

  // 删除歌曲
  const handleRemoveSong = (song) => {
    if (selectedPlaylist === 'favorites') {
      dataManager.removeFromFavorites(song.id, song.platform);
      showToastMessage('已从我喜欢的音乐中移除');
    } else if (selectedPlaylist.startsWith('playlist_')) {
      dataManager.removeSongFromPlaylist(selectedPlaylist, song.id, song.platform);
      showToastMessage('已从歌单中移除');
    }
    loadData(); // 重新加载数据
  };

  // 创建新歌单
  const handleCreatePlaylist = (name) => {
    const newPlaylist = dataManager.createPlaylist(name);
    setSelectedPlaylist(newPlaylist.id);
    loadData();
    if (onPlaylistsChange) {
      onPlaylistsChange(); // 刷新侧边栏歌单列表
    }
    showToastMessage(`歌单「${name}」创建成功`);
  };

  // 确认删除歌单
  const confirmDeletePlaylist = (playlist) => {
    setConfirmDialog({
      open: true,
      playlist: playlist
    });
  };

  // 删除歌单
  const handleDeletePlaylist = () => {
    const playlist = confirmDialog.playlist;
    if (playlist) {
      dataManager.deletePlaylist(playlist.id);
      if (selectedPlaylist === playlist.id) {
        setSelectedPlaylist('favorites');
      }
      loadData();
      if (onPlaylistsChange) {
        onPlaylistsChange(); // 刷新侧边栏歌单列表
      }
      showToastMessage(`歌单「${playlist.name}」已删除`);
    }
  };

  // 播放全部确认
  const handlePlayAllConfirm = async () => {
    if (songs.length > 0 && onAddSongsToQueue) {
      await onAddSongsToQueue(songs, true); // true表示替换播放队列
      showToastMessage(`已替换播放队列，正在播放 ${songs.length} 首歌曲`);
    }
    setPlayAllConfirmDialog({ open: false });
  };

  // 重置筛选
  const handleResetFilter = () => {
    setFilterOptions({
      searchTerm: '',
      artist: '',
      album: '',
      platform: 'all'
    });
  };

  // 清除最近播放
  const handleClearRecent = () => {
    dataManager.clearRecent();
    setClearRecentDialog({ open: false });
    showToastMessage('已清除所有最近播放记录');
    // 刷新歌单数据
    setSelectedPlaylist('recent'); // 触发重新渲染
  };

  // 获取可用的筛选选项
  const getFilterOptions = () => {
    const artists = [...new Set(allSongs.map(song => song.artist).filter(Boolean))];
    const albums = [...new Set(allSongs.map(song => song.album).filter(Boolean))];
    const platforms = [...new Set(allSongs.map(song => song.platform).filter(Boolean))];
    
    return { artists, albums, platforms };
  };

  const { artists, albums, platforms } = getFilterOptions();

  // 筛选歌单
  const filteredUserPlaylists = userPlaylists.filter(playlist =>
    playlist.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 格式化时长
  const formatDuration = (duration) => {
    return duration;
  };

  // 获取平台图标
  const getPlatformInfo = (platform) => {
    const platformInfo = {
      netease: { 
        icon: (
          <img 
            src="/icon/netease.png" 
            alt="网易云音乐" 
            className="w-5 h-5 object-contain"
          />
        )
      },
      kuwo: { 
        icon: (
          <img 
            src="/icon/kw.png" 
            alt="酷我音乐" 
            className="w-5 h-5 object-contain"
          />
        )
      },
      qq: { 
        icon: (
          <img 
            src="/icon/qq.png" 
            alt="QQ音乐" 
            className="w-5 h-5 object-contain"
          />
        )
      }
    };
    return platformInfo[platform] || { 
      icon: (
        <Music className="w-5 h-5 text-gray-600" />
      )
    };
  };

  // 播放列表项组件
  const PlaylistItem = ({ playlist, isSelected, onClick }) => {
    const Icon = playlist.icon;
    
    return (
      <button
        onClick={() => onClick(playlist.id)}
        className={`w-full text-left p-4 rounded-xl transition-all duration-200 group hover:bg-dark-100 ${
          isSelected ? 'bg-dark-100 shadow-sm' : ''
        }`}
      >
        <div className="flex items-center gap-4">
          {/* 图标/封面 */}
          <div className={`w-12 h-12 bg-gradient-to-br ${playlist.color} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}>
            <Icon size={24} className="text-white" />
          </div>
          
          {/* 歌单信息 */}
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold truncate transition-colors duration-200 ${
              isSelected ? 'text-primary-600' : 'text-dark-800 group-hover:text-primary-600'
            }`}>
              {playlist.name}
            </h3>
            <p className="text-sm text-dark-500 truncate">
              {playlist.count} 首歌曲
            </p>
          </div>
          
          {/* 播放按钮（悬停时显示） */}
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button 
              onClick={async (e) => {
                e.stopPropagation();
                const playlistData = getCurrentPlaylistData();
                if (playlistData && playlistData.songs.length > 0 && onAddSongsToQueue) {
                  await onAddSongsToQueue(playlistData.songs, true);
                  showToastMessage(`正在播放「${playlist.name}」`);
                }
              }}
              className="w-8 h-8 bg-primary-500 hover:bg-primary-600 text-white rounded-full flex items-center justify-center transition-colors duration-200"
              title="播放歌单"
            >
              <Play size={14} className="ml-0.5" />
            </button>
          </div>
        </div>
      </button>
    );
  };

  // 歌曲项组件
  const SongItem = ({ song, index, isPlaying, onClick }) => {
    const platformInfo = getPlatformInfo(song.platform);
    
    return (
      <div 
        onClick={() => onClick(song)}
        className="flex items-center gap-4 p-3 rounded-lg hover:bg-dark-100 transition-all duration-200 group cursor-pointer"
      >
        {/* 序号/播放状态 */}
        <div className="w-8 text-center">
          {isPlaying && currentSong?.id === song.id ? (
            <div className="w-4 h-4 mx-auto">
              <div className="flex items-center justify-center space-x-0.5">
                <div className="w-1 h-3 bg-primary-500 animate-pulse"></div>
                <div className="w-1 h-2 bg-primary-500 animate-pulse animation-delay-75"></div>
                <div className="w-1 h-4 bg-primary-500 animate-pulse animation-delay-150"></div>
              </div>
            </div>
          ) : (
            <span className="text-sm text-dark-400 group-hover:hidden">{index + 1}</span>
          )}
          <Play size={14} className="text-primary-500 hidden group-hover:block mx-auto" />
        </div>

        {/* 平台图标（替代封面） */}
        <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
          <div className="w-7 h-7 flex items-center justify-center">
            {platformInfo.icon}
          </div>
        </div>

        {/* 歌曲信息 */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-dark-800 truncate group-hover:text-primary-600 transition-colors duration-200">
            {song.name || song.title}
          </h4>
          <p className="text-sm text-dark-500 truncate">{song.artist}</p>
        </div>

        {/* 专辑 */}
        <div className="hidden md:block flex-1 min-w-0">
          <p className="text-sm text-dark-500 truncate">{song.album}</p>
        </div>

        {/* 时长 */}
        <div className="text-sm text-dark-400 w-12 text-right">
          {formatDuration(song.duration || '--:--')}
        </div>

        {/* 删除按钮 */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveSong(song);
          }}
          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 hover:text-red-400 rounded transition-all duration-200"
          title="移除"
        >
          <Trash2 size={16} className="text-red-500" />
        </button>
      </div>
    );
  };

  return (
    <div className="flex-1 bg-dark-50 overflow-hidden">
      <div className="flex h-full">
        {/* 左栏：歌单列表 */}
        <div className="w-80 bg-dark-50 border-r border-dark-200 flex flex-col">
          {/* 左栏头部 */}
          <div className="p-6 border-b border-dark-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-dark-800">音乐库</h2>
              <button 
                onClick={() => setCreateDialogOpen(true)}
                className="p-2 hover:bg-dark-100 rounded-lg transition-colors duration-200"
                title="创建歌单"
              >
                <Plus size={20} className="text-dark-600" />
              </button>
            </div>
            
            {/* 搜索栏 */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400" size={16} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="搜索歌单..."
                className="w-full pl-10 pr-4 py-2 bg-dark-100 border border-dark-200 rounded-lg text-dark-800 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-sm"
              />
            </div>
          </div>

          {/* 歌单列表 */}
          <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
            <div className="space-y-2">
              {/* 默认歌单 */}
              {getDefaultPlaylists().map((playlist) => (
                <PlaylistItem
                  key={playlist.id}
                  playlist={playlist}
                  isSelected={selectedPlaylist === playlist.id}
                  onClick={setSelectedPlaylist}
                />
              ))}
              
              {/* 分隔线 */}
              <div className="border-t border-dark-200 my-4"></div>
              
              {/* 用户歌单 */}
              <div className="mb-3 flex items-center justify-between px-4">
                <h3 className="text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  我的歌单
                </h3>
                <span className="text-xs text-dark-400">{userPlaylists.length}</span>
              </div>
              {filteredUserPlaylists.length > 0 ? (
                filteredUserPlaylists.map((playlist) => (
                  <PlaylistItem
                    key={playlist.id}
                    playlist={{
                      ...playlist,
                      icon: Music,
                      color: 'from-purple-500 to-violet-500',
                      count: playlist.songs?.length || 0
                    }}
                    isSelected={selectedPlaylist === playlist.id}
                    onClick={setSelectedPlaylist}
                  />
                ))
              ) : (
                searchTerm && (
                  <div className="text-center py-4">
                    <p className="text-sm text-dark-400">未找到匹配的歌单</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* 右栏：歌曲列表 */}
        <div className="flex-1 flex flex-col">
          {/* 右栏头部 */}
          <div className="p-6 border-b border-dark-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-dark-800 mb-1">
                  {currentPlaylist?.name}
                </h1>
                <p className="text-dark-500">
                  {songs.length} 首歌曲 • {currentPlaylist?.description}
                </p>
              </div>
              
              {/* 操作按钮 */}
              <div className="flex items-center gap-3">

                <button 
                  onClick={() => setFilterDialogOpen(true)}
                  className="p-2 hover:bg-dark-100 rounded-lg transition-colors duration-200"
                  title="筛选歌曲"
                >
                  <Filter size={20} className="text-dark-600" />
                </button>
                
                {/* 只有最近播放才显示清除按钮 */}
                {selectedPlaylist === 'recent' && (
                  <button 
                    onClick={() => setClearRecentDialog({ open: true })}
                    className="p-2 hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-colors duration-200"
                    title="清除最近播放"
                  >
                    <RotateCcw size={20} className="text-red-500" />
                  </button>
                )}
                
                {/* 只有用户创建的歌单才显示删除按钮 */}
                {!['favorites', 'recent'].includes(selectedPlaylist) && (
                  <button 
                    onClick={() => {
                      const playlist = userPlaylists.find(p => p.id === selectedPlaylist);
                      if (playlist) {
                        confirmDeletePlaylist(playlist);
                      }
                    }}
                    className="p-2 hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-colors duration-200"
                    title="删除歌单"
                  >
                    <Trash2 size={20} className="text-red-500" />
                  </button>
                )}
                
                <button 
                  onClick={() => {
                    if (songs.length > 0) {
                      setPlayAllConfirmDialog({ open: true });
                    }
                  }}
                  disabled={songs.length === 0}
                  className="p-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="播放全部"
                >
                  <Play size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* 歌曲列表 */}
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            <div className="p-6">
              {/* 列表头部 */}
              <div className="flex items-center gap-4 px-3 py-2 text-sm text-dark-500 border-b border-dark-200 mb-2">
                <div className="w-8">#</div>
                <div className="w-10"></div>
                <div className="flex-1">标题</div>
                <div className="hidden md:block flex-1">专辑</div>
                <div className="w-12 text-right">时长</div>
                <div className="w-8"></div>
              </div>

              {/* 歌曲列表 */}
              {songs.length > 0 ? (
                <div className="space-y-1">
                  {songs.map((song, index) => (
                    <SongItem
                      key={`${song.id}_${song.platform}`}
                      song={song}
                      index={index}
                      isPlaying={isPlaying}
                      onClick={(song) => {
                        if (onPlaySong) {
                          onPlaySong(song);
                        } else {
                          setCurrentSong(song);
                          setIsPlaying(true);
                          // 添加到最近播放
                          dataManager.addToRecent(song);
                        }
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Music size={48} className="text-dark-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-dark-600 mb-2">
                    {selectedPlaylist === 'favorites' ? '还没有收藏音乐' : '歌单为空'}
                  </h3>
                  <p className="text-dark-500">
                    {selectedPlaylist === 'favorites' 
                      ? '去搜索页面收藏一些喜欢的音乐吧'
                      : '从搜索结果添加歌曲到这个歌单'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 确认对话框 */}
        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={() => setConfirmDialog({ open: false, playlist: null })}
          onConfirm={handleDeletePlaylist}
          title="删除歌单"
          message={`确定要删除歌单「${confirmDialog.playlist?.name}」吗？删除后将无法恢复。`}
          confirmText="删除"
          cancelText="取消"
          type="danger"
        />

        {/* 播放全部确认对话框 */}
        <ConfirmDialog
          isOpen={playAllConfirmDialog.open}
          onClose={() => setPlayAllConfirmDialog({ open: false })}
          onConfirm={handlePlayAllConfirm}
          title="播放全部"
          message="该操作会完全替换播放列表，是否确认？"
          confirmText="确认"
          cancelText="取消"
          type="warning"
        />

        {/* 清除最近播放确认对话框 */}
        <ConfirmDialog
          isOpen={clearRecentDialog.open}
          onClose={() => setClearRecentDialog({ open: false })}
          onConfirm={handleClearRecent}
          title="清除最近播放"
          message="确定要清除所有最近播放记录吗？此操作无法撤销。"
          confirmText="清除"
          cancelText="取消"
          type="danger"
        />

        {/* 创建歌单对话框 */}
        <CreatePlaylistDialog
          isOpen={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onConfirm={handleCreatePlaylist}
        />

        {/* 筛选对话框 */}
        {filterDialogOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-dark-50 rounded-2xl p-6 w-96 max-w-[90vw] shadow-2xl">
              {/* 头部 */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-dark-800">筛选歌曲</h3>
                <button
                  onClick={() => setFilterDialogOpen(false)}
                  className="p-1 hover:bg-dark-100 rounded-lg transition-colors duration-200"
                >
                  <X size={20} className="text-dark-600" />
                </button>
              </div>

              {/* 筛选选项 */}
              <div className="space-y-4">
                {/* 搜索框 */}
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-2">搜索歌曲</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400" size={16} />
                    <input
                      type="text"
                      value={filterOptions.searchTerm}
                      onChange={(e) => setFilterOptions(prev => ({ ...prev, searchTerm: e.target.value }))}
                      placeholder="搜索歌名或歌手..."
                      className="w-full pl-10 pr-4 py-2 bg-dark-100 border border-dark-200 rounded-lg text-dark-800 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all duration-200"
                    />
                  </div>
                </div>

                {/* 歌手筛选 */}
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-2">歌手</label>
                  <select
                    value={filterOptions.artist}
                    onChange={(e) => setFilterOptions(prev => ({ ...prev, artist: e.target.value }))}
                    className="w-full px-3 py-2 bg-dark-100 border border-dark-200 rounded-lg text-dark-800 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all duration-200"
                  >
                    <option value="">全部歌手</option>
                    {artists.map(artist => (
                      <option key={artist} value={artist}>{artist}</option>
                    ))}
                  </select>
                </div>

                {/* 专辑筛选 */}
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-2">专辑</label>
                  <select
                    value={filterOptions.album}
                    onChange={(e) => setFilterOptions(prev => ({ ...prev, album: e.target.value }))}
                    className="w-full px-3 py-2 bg-dark-100 border border-dark-200 rounded-lg text-dark-800 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all duration-200"
                  >
                    <option value="">全部专辑</option>
                    {albums.map(album => (
                      <option key={album} value={album}>{album}</option>
                    ))}
                  </select>
                </div>

                {/* 来源筛选 */}
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-2">来源</label>
                  <select
                    value={filterOptions.platform}
                    onChange={(e) => setFilterOptions(prev => ({ ...prev, platform: e.target.value }))}
                    className="w-full px-3 py-2 bg-dark-100 border border-dark-200 rounded-lg text-dark-800 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all duration-200"
                  >
                    <option value="all">全部来源</option>
                    {platforms.map(platform => (
                      <option key={platform} value={platform}>
                        {platform === 'netease' ? '网易云音乐' : 
                         platform === 'qq' ? 'QQ音乐' : 
                         platform === 'kuwo' ? '酷我音乐' : platform}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 底部按钮 */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-dark-200">
                <button
                  onClick={handleResetFilter}
                  className="px-4 py-2 text-dark-600 hover:text-dark-800 hover:bg-dark-100 rounded-lg transition-colors duration-200"
                >
                  重置
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setFilterDialogOpen(false)}
                    className="px-4 py-2 text-dark-600 hover:text-dark-800 hover:bg-dark-100 rounded-lg transition-colors duration-200"
                  >
                    取消
                  </button>
                  <button
                    onClick={() => setFilterDialogOpen(false)}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200"
                  >
                    确定
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 提示消息 */}
        {showToast && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
            <div className={`px-6 py-3 rounded-2xl shadow-lg backdrop-blur-md ${
              showToast.type === 'success' 
                ? 'bg-green-500/90 text-white' 
                : 'bg-red-500/90 text-white'
            }`}>
              <p className="font-medium">{showToast.message}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LibraryPage;

